import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';

interface EmployeeAppraisal {
  id: string;
  appraisal_number: string;
  employee_id: string;
  template_id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  cycle_name: string;
  status: string;
  overall_rating: number;
  grade: string;
}

interface PendingApproval {
  approvalInstanceId: string;
  appraisal_number: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  currentLevel: number;
  finalLevel: number;
}

interface Parameter {
  id: string;
  parameter_name: string;
  weightage: number;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search
    ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase()))
    : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const fetchCycleOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/pas/cycles');
  const cycles = res.data.cycles || [];
  const filtered = search
    ? cycles.filter((c: any) => c.cycle_name.toLowerCase().includes(search.toLowerCase()))
    : cycles;
  return filtered.map((c: any) => ({ value: c.id, label: c.cycle_name }));
};

const fetchTemplateOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/pas/templates');
  const templates = res.data.templates || [];
  const filtered = search
    ? templates.filter((t: any) => t.template_name.toLowerCase().includes(search.toLowerCase()))
    : templates;
  return filtered.map((t: any) => ({ value: t.id, label: t.template_name }));
};

export default function EmployeeAppraisals() {
  const [appraisals, setAppraisals] = useState<EmployeeAppraisal[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [scoreTarget, setScoreTarget] = useState<EmployeeAppraisal | null>(null);
  const [historyTarget, setHistoryTarget] = useState<EmployeeAppraisal | null>(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    appraisalCycleId: '',
    templateId: '',
    reportingManagerId: '',
    appraisalPeriodStart: '',
    appraisalPeriodEnd: '',
  });

  useEffect(() => {
    fetchAppraisals();
    fetchPendingApprovals();
  }, []);

  const fetchAppraisals = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pas/appraisals');
      setAppraisals(response.data.appraisals || []);
    } catch (error) {
      console.error('Failed to fetch employee appraisals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await api.get('/pas/appraisals/my-approvals');
      setPendingApprovals(res.data.approvals || []);
    } catch (error) {
      console.error('Failed to fetch pending appraisal approvals:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/pas/appraisals', formData);
      setFormData({
        employeeId: '',
        appraisalCycleId: '',
        templateId: '',
        reportingManagerId: '',
        appraisalPeriodStart: '',
        appraisalPeriodEnd: '',
      });
      setShowForm(false);
      fetchAppraisals();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create employee appraisal');
    }
  };

  const handleSubmitForApproval = async (appraisalId: string) => {
    try {
      await api.put(`/pas/appraisals/${appraisalId}/submit`);
      alert('Appraisal submitted for approval');
      fetchAppraisals();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to submit appraisal');
    }
  };

  const handleApprovalAction = async (appraisalId: string, action: 'APPROVED' | 'REJECTED') => {
    try {
      await api.put(`/pas/appraisals/${appraisalId}/approve`, { action });
      fetchPendingApprovals();
      fetchAppraisals();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to act on approval');
    }
  };

  const columns: Column<EmployeeAppraisal>[] = [
    { key: 'appraisal_number', header: 'Apr. No.' },
    { key: 'employee', header: 'Employee', render: (row) => `${row.first_name} ${row.last_name}` },
    { key: 'cycle_name', header: 'Cycle' },
    { key: 'overall_rating', header: 'Rating', render: (row) => row.overall_rating ?? '—' },
    { key: 'grade', header: 'Grade', render: (row) => row.grade ?? '—' },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          {row.status === 'DRAFT' && (
            <button onClick={() => setScoreTarget(row)} className="text-blue-600 hover:underline text-sm">
              Score
            </button>
          )}
          <button onClick={() => setHistoryTarget(row)} className="text-gray-600 hover:underline text-sm">
            History
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Employee Appraisals</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Create Appraisal'}</Button>
      </div>

      {pendingApprovals.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-amber-900 mb-3">
            Pending Appraisals Awaiting Your Approval ({pendingApprovals.length})
          </h2>
          <ul className="space-y-2">
            {pendingApprovals.map((approval) => (
              <li
                key={approval.approvalInstanceId}
                className="flex justify-between items-center bg-white rounded-lg p-3 text-sm"
              >
                <span>
                  {approval.first_name} {approval.last_name} ({approval.employee_code}) —{' '}
                  <strong>{approval.appraisal_number}</strong> — level {approval.currentLevel}/{approval.finalLevel}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprovalAction(approval.approvalInstanceId, 'APPROVED')}
                    className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleApprovalAction(approval.approvalInstanceId, 'REJECTED')}
                    className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <LookupSelect
                label="Employee"
                value={formData.employeeId}
                onChange={(value) => setFormData({ ...formData, employeeId: value })}
                fetchOptions={fetchEmployeeOptions}
                placeholder="Search employee..."
                required
              />
              <LookupSelect
                label="Appraisal Cycle"
                value={formData.appraisalCycleId}
                onChange={(value) => setFormData({ ...formData, appraisalCycleId: value })}
                fetchOptions={fetchCycleOptions}
                placeholder="Search cycle..."
                required
              />
              <LookupSelect
                label="Template"
                value={formData.templateId}
                onChange={(value) => setFormData({ ...formData, templateId: value })}
                fetchOptions={fetchTemplateOptions}
                placeholder="Search template..."
                required
              />
              <LookupSelect
                label="Reporting Manager"
                value={formData.reportingManagerId}
                onChange={(value) => setFormData({ ...formData, reportingManagerId: value })}
                fetchOptions={fetchEmployeeOptions}
                placeholder="Search manager..."
              />
              <input
                type="date"
                value={formData.appraisalPeriodStart}
                onChange={(e) => setFormData({ ...formData, appraisalPeriodStart: e.target.value })}
                className="px-4 py-2 border rounded-lg"
              />
              <input
                type="date"
                value={formData.appraisalPeriodEnd}
                onChange={(e) => setFormData({ ...formData, appraisalPeriodEnd: e.target.value })}
                className="px-4 py-2 border rounded-lg"
              />
            </div>
            <Button type="submit" className="w-full">
              Create Appraisal
            </Button>
          </form>
        </div>
      )}

      <Table columns={columns} rows={appraisals} loading={loading} emptyMessage="No appraisals yet" />

      {scoreTarget && (
        <ScoreModal
          appraisal={scoreTarget}
          onClose={() => setScoreTarget(null)}
          onSubmitted={() => {
            setScoreTarget(null);
            fetchAppraisals();
          }}
          onSubmitForApproval={handleSubmitForApproval}
        />
      )}

      {historyTarget && (
        <HistoryModal employeeId={historyTarget.employee_id} onClose={() => setHistoryTarget(null)} />
      )}
    </div>
  );
}

function ScoreModal({
  appraisal,
  onClose,
  onSubmitForApproval,
}: {
  appraisal: EmployeeAppraisal;
  onClose: () => void;
  onSubmitted: () => void;
  onSubmitForApproval: (id: string) => void;
}) {
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [liveScore, setLiveScore] = useState<{ overallRating: number | null; grade: string | null }>({
    overallRating: null,
    grade: null,
  });

  useEffect(() => {
    loadParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadParameters = async () => {
    if (!appraisal.template_id) return;
    const res = await api.get(`/pas/templates/${appraisal.template_id}/parameters`);
    setParameters(res.data.parameters || []);
  };

  const rateAndRefresh = async (parameterId: string, value: number) => {
    setRatings((prev) => ({ ...prev, [parameterId]: value }));
    try {
      await api.post(`/pas/appraisals/${appraisal.id}/responses`, {
        parameterId,
        responseType: 'MANAGER',
        numericRating: value,
      });
      const scoreRes = await api.get(`/pas/appraisals/${appraisal.id}/score`);
      setLiveScore(scoreRes.data);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to record rating');
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Score ${appraisal.appraisal_number}`}>
      <div className="space-y-4">
        {parameters.length === 0 && (
          <p className="text-sm text-gray-500">
            Loading parameters... (if empty, ensure the template has KRAs configured with weightage on the
            Appraisal Templates page)
          </p>
        )}
        {parameters.map((param) => (
          <div key={param.id} className="flex items-center justify-between gap-4">
            <span className="text-sm">
              {param.parameter_name} <span className="text-gray-400">({param.weightage}%)</span>
            </span>
            <select
              value={ratings[param.id] || ''}
              onChange={(e) => rateAndRefresh(param.id, Number(e.target.value))}
              className="border rounded-lg px-3 py-1"
            >
              <option value="">Rate...</option>
              {[1, 2, 3, 4, 5].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        ))}

        {liveScore.overallRating !== null && (
          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
            Live weighted score: <strong>{liveScore.overallRating}</strong> — Grade{' '}
            <strong>{liveScore.grade}</strong>
          </div>
        )}

        <Button onClick={() => onSubmitForApproval(appraisal.id)} className="w-full">
          Submit for Approval
        </Button>
      </div>
    </Modal>
  );
}

function HistoryModal({ employeeId, onClose }: { employeeId: string; onClose: () => void }) {
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    api.get(`/pas/performance-history/${employeeId}`).then((res) => setHistory(res.data.history || []));
  }, [employeeId]);

  return (
    <Modal isOpen={true} onClose={onClose} title="Performance History">
      {history.length === 0 && <p className="text-sm text-gray-500">No history yet</p>}
      <ul className="space-y-2">
        {history.map((h) => (
          <li key={h.id} className="flex justify-between items-center bg-gray-50 rounded-lg p-3 text-sm">
            <span>{h.appraisal_year}</span>
            <span>
              Rating: <strong>{h.overall_rating}</strong> · Grade: <strong>{h.grade}</strong>
            </span>
            <span
              className={
                h.performance_trend === 'UP'
                  ? 'text-green-600'
                  : h.performance_trend === 'DOWN'
                  ? 'text-red-600'
                  : 'text-gray-500'
              }
            >
              {h.performance_trend}
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
